import { useEffect, useRef } from "preact/hooks";
import KeenSlider from "keen-slider";
import type { KeenSliderInstance } from "keen-slider";
import "keen-slider/keen-slider.min.css";
import "./index.scss";

export interface KeenSliderOptions {
  breakpoints?: Record<number, {
    slides: {
      perView?: number | "auto";
      spacing?: number;
      origin?: "auto" | "center";
    };
  }>;
  drag?: boolean;
  slides?: {
    perView?: number | "auto";
    spacing?: number;
    origin?: "auto" | "center";
  };
  loop?: boolean;
  mode?: "snap" | "free" | "free-snap";
  autoplay?: {
    enabled: boolean;
    duration?: number; // Duration in seconds
    distance?: number; // Number of slides to move
    pauseOnHover?: boolean;
  };
  navigation?: {
    enabled: boolean;
    prevButton?: string;
    nextButton?: string;
  };
  created?: (slider: KeenSliderInstance) => void;
  initial?: number;
}

export interface KeenSliderProps {
  children: preact.ComponentChildren;
  className?: string;
  options?: KeenSliderOptions;
  onReady?: (instance: KeenSliderInstance) => void;
}

export const KeenSliderComponent = ({ children, className = "", options = {}, onReady }: KeenSliderProps) => {
  const sliderRef = useRef<HTMLDivElement>(null);
  const keenSliderRef = useRef<KeenSliderInstance | null>(null);

  useEffect(() => {
    if (sliderRef.current) {
      const defaultOptions: any = {
        drag: options.drag !== undefined ? options.drag : true,
        mode: options.mode || "snap",
        slides: {
          perView: options.slides?.perView || "auto",
          spacing: options.slides?.spacing || 16,
          origin: options.slides?.origin || "auto",
        },
        breakpoints: options.breakpoints,
        loop: options.loop || false,
        initial: options.initial || 0,
      };

      // Add custom created callback if provided
      if (options.created) {
        defaultOptions.created = options.created;
      }

      // Add autoplay configuration if enabled
      if (options.autoplay?.enabled) {
        const duration = options.autoplay.duration || 5;
        const distance = options.autoplay.distance || 1;
        const animation = { duration: duration * 1000, easing: (t: number) => t };

        defaultOptions.created = (slider: KeenSliderInstance) => {
          slider.moveToIdx(distance, true, animation);
        };

        defaultOptions.updated = (slider: KeenSliderInstance) => {
          slider.moveToIdx(slider.track.details.abs + distance, true, animation);
        };

        defaultOptions.animationEnded = (slider: KeenSliderInstance) => {
          slider.moveToIdx(slider.track.details.abs + distance, true, animation);
        };
      }

      // Add navigation configuration if enabled
      if (options.navigation?.enabled) {
        const prevButton = options.navigation.prevButton ? document.querySelector(options.navigation.prevButton) : null;
        const nextButton = options.navigation.nextButton ? document.querySelector(options.navigation.nextButton) : null;

        if (prevButton) {
          prevButton.addEventListener('click', () => {
            if (keenSliderRef.current) {
              keenSliderRef.current.prev();
            }
          });
        }

        if (nextButton) {
          nextButton.addEventListener('click', () => {
            if (keenSliderRef.current) {
              keenSliderRef.current.next();
            }
          });
        }
      }

      keenSliderRef.current = new KeenSlider(sliderRef.current, defaultOptions);

      if (onReady && keenSliderRef.current) {
        onReady(keenSliderRef.current);
      }

      // Add hover pause functionality if enabled
      if (options.autoplay?.enabled && options.autoplay.pauseOnHover) {
        const sliderElement = sliderRef.current;
        const slider = keenSliderRef.current;

        const handleMouseEnter = () => {
          slider.animator.stop();
        };

        const handleMouseLeave = () => {
          const distance = options.autoplay?.distance || 1;
          const duration = options.autoplay?.duration || 5;
          const animation = { duration: duration * 1000, easing: (t: number) => t };
          slider.moveToIdx(slider.track.details.abs + distance, true, animation);
        };

        sliderElement.addEventListener('mouseenter', handleMouseEnter);
        sliderElement.addEventListener('mouseleave', handleMouseLeave);

        return () => {
          sliderElement.removeEventListener('mouseenter', handleMouseEnter);
          sliderElement.removeEventListener('mouseleave', handleMouseLeave);
          if (slider) slider.destroy();
        };
      }

      return () => {
        if (keenSliderRef.current) {
          keenSliderRef.current.destroy();
        }
      };
    }
  }, [options, onReady]);

  return (
    <div ref={sliderRef} className={`keen-slider ${className}`}>
      {children}
    </div>
  );
}; 